import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, KeyRound, ShieldAlert } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError, api } from '@/lib/api';
import { Button, Field, Input, LoadingState } from '@/components/ui';
import { PublicAuthLayout } from '@/components/layout/PublicAuthLayout';

interface InspectResponse {
  valid: boolean;
  emailHint: string;
}

/** Controles de robustesse affiches en direct sous le champ. */
const RULES = [
  { key: 'length', test: (value: string) => value.length >= 10 },
  { key: 'upper', test: (value: string) => /[A-Z]/.test(value) },
  { key: 'lower', test: (value: string) => /[a-z]/.test(value) },
  { key: 'digit', test: (value: string) => /\d/.test(value) },
  { key: 'special', test: (value: string) => /[^A-Za-z0-9]/.test(value) },
] as const;

/**
 * Definition d'un nouveau mot de passe depuis le lien recu par e-mail.
 * Le jeton est verifie avant d'afficher le formulaire, afin de signaler
 * immediatement un lien expire ou deja utilise.
 */
export function ResetPasswordPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<'checking' | 'valid' | 'invalid' | 'done'>('checking');
  const [emailHint, setEmailHint] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await api.get<InspectResponse>('/auth/reset-password', { query: { token } });
        if (cancelled) return;
        setEmailHint(response.emailHint);
        setStatus('valid');
      } catch {
        if (!cancelled) setStatus('invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const satisfied = RULES.filter((rule) => rule.test(password)).length;

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setStatus('done');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.localized() : t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <PublicAuthLayout title={t('auth.reset.title')}>
        <LoadingState label={t('auth.reset.checking')} />
      </PublicAuthLayout>
    );
  }

  if (status === 'invalid') {
    return (
      <PublicAuthLayout title={t('auth.reset.invalidTitle')}>
        <div className="space-y-4 rounded-2xl border border-border bg-surface p-5">
          <p className="flex items-start gap-3 text-sm text-ink">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden />
            <span>{t('auth.reset.invalidBody')}</span>
          </p>
          <Link to="/forgot-password" className="block">
            <Button className="w-full justify-center">{t('auth.reset.requestNew')}</Button>
          </Link>
        </div>
      </PublicAuthLayout>
    );
  }

  if (status === 'done') {
    return (
      <PublicAuthLayout title={t('auth.reset.doneTitle')}>
        <div className="space-y-4 rounded-2xl border border-border bg-surface p-5">
          <p className="flex items-start gap-3 text-sm text-ink">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
            <span>{t('auth.reset.doneBody')}</span>
          </p>
          <Button onClick={() => navigate('/login', { replace: true })} className="w-full justify-center">
            {t('auth.signIn')}
          </Button>
        </div>
      </PublicAuthLayout>
    );
  }

  return (
    <PublicAuthLayout title={t('auth.reset.title')} description={t('auth.reset.subtitle', { email: emailHint })}>
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
        <Field label={t('auth.newPassword')} htmlFor="password" required>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            minLength={10}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <ul className="grid gap-1.5 rounded-xl bg-surface-muted px-4 py-3 sm:grid-cols-2">
          {RULES.map((rule) => {
            const ok = rule.test(password);
            return (
              <li key={rule.key} className={`flex items-center gap-2 text-xs ${ok ? 'text-success' : 'text-ink-muted'}`}>
                <CheckCircle2 className={`h-3.5 w-3.5 shrink-0 ${ok ? '' : 'opacity-35'}`} aria-hidden />
                {t(`auth.reset.rules.${rule.key}`)}
              </li>
            );
          })}
        </ul>

        <Field label={t('auth.confirmPassword')} htmlFor="confirm" required>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            invalid={confirmation.length > 0 && confirmation !== password}
          />
        </Field>

        {error ? (
          <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          size="lg"
          loading={submitting}
          disabled={satisfied < RULES.length}
          icon={<KeyRound className="h-4 w-4" />}
          className="w-full justify-center"
        >
          {t('auth.reset.submit')}
        </Button>
      </form>
    </PublicAuthLayout>
  );
}
